// @ts-ignore
import { CompanyType, RequestTypeCatalog } from "request-form-lib";
import axiosInstance from "@/axios";
import { useSearchParams } from 'react-router-dom';
import { useRouter } from "@/Router/router";
import { Box, Breadcrumbs, InputAdornment, Link, TextField, Typography } from "@mui/material";
import { companyType } from "@/Utils/environment";
import { getXCenterStyles } from "vig-ui-library";
import { IoSearchOutline } from "react-icons/io5";
import { useEffect, useRef, useState } from "react";

type ParentTag = {
    id: string | number;
    name?: string | null;
};

export const RequestCatalogBuilder = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [parentTags, setParentTags] = useState<ParentTag[]>([]);
    const [availableWidth, setAvailableWidth] = useState<number | undefined>(undefined);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const { navigateToAddNewRequest } = useRouter();

    const rt = searchParams.get('rt');

    const q = searchParams.get('q');

    const updateSearchParams = (key: string, value: string | null) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (value) newSearchParams.set(key, value);
        else newSearchParams.delete(key);
        setSearchParams(newSearchParams);
    }

    const onRequestTypeSelected = (requestType: string) => {
        navigateToAddNewRequest(requestType);
    }

    const onParentTagSelected = (parentTag: string | null) => {
        updateSearchParams('rt', parentTag);
    }

    const setQuery = (query: string) => {
        updateSearchParams("q", query || null);
    };

    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const updateWidth = () => {
            const rectWidth = element.getBoundingClientRect().width;
            const computedStyle = window.getComputedStyle(element);
            const paddingLeft = parseFloat(computedStyle.paddingLeft || "0");
            const paddingRight = parseFloat(computedStyle.paddingRight || "0");
            const contentWidth = Math.max(0, rectWidth - paddingLeft - paddingRight);
            setAvailableWidth(contentWidth);
        };

        updateWidth();

        const observer = new ResizeObserver(() => updateWidth());
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    return <Box
        ref={containerRef}
        id="request-catalog-builder"
        sx={{
            padding: { xs: "12px", sm: "24px", md: "40px" },
            flexGrow: "1",
            height: "calc(100vh - 64px)",
            minHeight: 0,
            backgroundColor: "#f2f2f2",
            ...getXCenterStyles(),
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
        }}
    >
        <Box sx={{ width: "100%", marginBottom: 2 }}>
            <TextField
                sx={{ width: "100%" }}
                id="search-requests"
                label="Hladať žiadosť"
                variant="filled"
                value={q || ""}
                onChange={(e) => setQuery(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <IoSearchOutline />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>

        {!!parentTags.length && (
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: 3 }}>
                <Link underline="hover" onClick={() => onParentTagSelected(null)}>
                    Všetky kategórie
                </Link>

                {parentTags.map((parent, index) => {
                    const isLast = parentTags.length - 1 === index;

                    if (!isLast) {
                        return (
                            <Link
                                key={String(parent.id)}
                                underline="hover"
                                onClick={() => onParentTagSelected(String(parent.id))}
                            >
                                {parent.name || ""}
                            </Link>
                        );
                    }

                    return <Typography key={String(parent.id)}>{parent.name || ""}</Typography>;
                })}
            </Breadcrumbs>
        )}

        <RequestTypeCatalog
            axiosInstance={axiosInstance}
            requestTagId={rt ?? undefined}
            query={q}
            onRequestTypeSelected={onRequestTypeSelected}
            onParentTagSelected={onParentTagSelected}
            onParentTagsChange={(tags: ParentTag[]) => setParentTags(tags)}
            companyType={companyType as CompanyType}
            availableWidth={availableWidth}
        />
    </Box>
};